/**
 * BodySpriteField — atlas sprite pool for round bodies (planets, moons,
 * sub-moons). Each body carries a 0..1 `sprite` seed mapped to a stable atlas
 * frame, is scaled to its radius, and dims when mined out. Sprites are pooled
 * and reconciled to the live body list each frame.
 */
import { Assets, Container, Rectangle, Sprite, Texture } from "pixi.js";

export interface FrameRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SpriteBody {
  id: string;
  pos: { x: number; y: number };
  radius: number;
  sprite: number; // 0..1 seed → atlas frame
  ore?: number; // optional (central planet has none); mined-out bodies dim
}

export class BodySpriteField {
  private container = new Container();
  private frames: Texture[] = [];
  private pool: Sprite[] = [];
  private active = new Map<string, Sprite>();

  constructor(
    private url: string,
    private rects: readonly FrameRect[],
  ) {}

  get view(): Container {
    return this.container;
  }

  async load(): Promise<void> {
    const sheet: Texture = await Assets.load(this.url);
    this.frames = this.rects.map(
      (f) =>
        new Texture({
          source: sheet.source,
          frame: new Rectangle(f.x, f.y, f.w, f.h),
        }),
    );
  }

  sync(items: readonly SpriteBody[]): void {
    const present = new Set<string>();
    for (const it of items) {
      present.add(it.id);
      let s = this.active.get(it.id);
      if (!s) {
        s = this.pool.pop() ?? new Sprite();
        s.anchor.set(0.5);
        s.visible = true;
        this.container.addChild(s);
        const tex =
          this.frames[Math.floor(it.sprite * this.frames.length)] ??
          this.frames[0];
        if (tex) {
          s.texture = tex;
          s.scale.set((it.radius * 2) / Math.max(tex.width, tex.height));
        }
        this.active.set(it.id, s);
      }
      s.position.set(it.pos.x, it.pos.y);
      s.alpha = (it.ore ?? 1) > 0 ? 1 : 0.4;
    }
    for (const [id, s] of this.active) {
      if (!present.has(id)) {
        s.visible = false;
        this.container.removeChild(s);
        this.pool.push(s);
        this.active.delete(id);
      }
    }
  }

  releaseAll(): void {
    for (const s of this.active.values()) {
      s.visible = false;
      this.container.removeChild(s);
      this.pool.push(s);
    }
    this.active.clear();
  }
}
